import { FC, useMemo } from "react";
import { Dialog, DialogContent, Fab, Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import Loader from "@components/Loader/Loader";
import CallEndOutlinedIcon from "@mui/icons-material/CallEndOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import { useGetCompanionsQuery } from "@store/companionApi";
import { Reminder } from "@models/reminder";
import { useTranslation } from "react-i18next";
import CallerImage from "@components/CallerImage";

interface IncomingCallDialogProps {
  open: boolean;
  loading: boolean;
  onClose: () => void;
  onAccept: () => void;
  reminder: Reminder | null;
}

const IncomingCallDialog: FC<IncomingCallDialogProps> = ({
  open,
  loading,
  onClose,
  onAccept,
  reminder,
}) => {
  const { t } = useTranslation();
  const patientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const userId = useSelector((state: RootState) => state.auth.user?.id);
  const { data: companions } = useGetCompanionsQuery(
    { patientId: patientId!, userId: userId! },
    {
      skip: !patientId,
    },
  );

  const companion = useMemo(() => {
    return companions?.data.find(({ isDefault }) => isDefault);
  }, [companions]);

  const handleClose = (_: Event, reason: string) => {
    if (reason && reason === "backdropClick") {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={true}
      PaperProps={{
        style: {
          backgroundColor: "#171a1fff",
          boxShadow: "none",
        },
      }}
    >
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {reminder ? (
          <Stack
            alignItems="center"
            justifyContent="space-between"
            gap="12px"
            height="95dvh"
            maxHeight="600px"
            color="#fff"
          >
            <Stack gap="12px" textAlign="center">
              <Typography
                fontSize={{ xs: "30px", sm: "40px" }}
                lineHeight="56px"
                fontWeight="400"
              >
                {t("incomingCall.caller", {
                  name: companion?.name || "",
                })}
              </Typography>
              <Typography fontSize="20px" lineHeight="30px" fontWeight="400">
                {reminder.title}
              </Typography>
            </Stack>

            <>
              {companion && (
                <CallerImage src={companion.avatar?.imageUrl || ""} alt="" />
              )}
            </>

            <Stack
              flexDirection="row"
              gap="54px"
              justifyContent="center"
              padding="24px"
            >
              <Fab
                size="large"
                color="error"
                onClick={onClose}
                disabled={loading}
                sx={{
                  "&.Mui-disabled": {
                    backgroundColor: "error.main",
                    color: "error.contrastText",
                    opacity: 0.4,
                  },
                }}
              >
                <CallEndOutlinedIcon />
              </Fab>
              <Fab
                size="large"
                color="success"
                onClick={onAccept}
                disabled={loading}
                sx={{
                  "&.Mui-disabled": {
                    backgroundColor: "success.main",
                    color: "success.contrastText",
                    opacity: 0.4,
                  },
                }}
              >
                <CallOutlinedIcon />
              </Fab>
            </Stack>
          </Stack>
        ) : (
          <Loader />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default IncomingCallDialog;
